import { FiFeather } from 'react-icons/fi';

export const subjunctiveMoodData = {
  courseId: 'subjunctive-mood',
  courseType: 'grammar',
  title: 'Subjunctive Mood',
  icon: FiFeather,
  overview: {
    impact: 'Used in formal recommendations and hypothetical statements',
    focus: 'Base verb after certain verbs and expressions',
    uses: 'Writing Task 2 recommendations',
    duration: '20 minutes'
  },
  sections: [
    {
      title: 'Mandative Subjunctive',
      content: `<p>After verbs/adjectives of recommendation, the base verb is used (no -s, no past):</p>`,
      examples: [
        {
          label: 'Formal',
          text: 'I recommend that she study abroad. (NOT studies)'
        },
        {
          label: 'Formal',
          text: 'It is essential that he be on time. (NOT is)'
        }
      ],
      points: [
        'Verbs: suggest, recommend, insist, demand, propose, request',
        'Adjectives: essential, important, necessary, vital, crucial',
        '\'I suggest that the government invest more in education.\''
      ]
    },
    {
      title: 'Were-Subjunctive',
      content: `<p>Using 'were' instead of 'was' in hypothetical situations:</p>`,
      examples: [
        {
          label: 'Formal',
          text: 'If I were the president, I would change the law.'
        },
        {
          label: 'Formal',
          text: 'She acts as if she were the boss.'
        }
      ]
    }
  ],
  exercises: [
    {
      title: 'Choose the Correct Form',
      type: 'select',
      instructions: 'Formal subjunctive or indicative?',
      questions: [
        {
          text: 'It is crucial that he ___ present at the meeting.',
          options: [
            'be',
            'is',
            'was'
          ],
          answer: 'be',
          explanation: 'Mandative subjunctive after \'crucial that\': base verb \'be\'.'
        },
        {
          text: 'I suggest that she ___ the course.',
          options: [
            'take',
            'takes',
            'took'
          ],
          answer: 'take',
          explanation: 'Subjunctive after \'suggest that\': base verb \'take\'.'
        }
      ]
    }
  ],
  tips: [
    'Use base verb after suggest/recommend/insist + that',
    'No -s for third person: \'I suggest he study\' (not studies)',
    'Were-subjunctive for hypotheticals: \'If I were...\'',
    'Shows formal register awareness for Band 8+'
  ],
  dailyPractice: [
    'Practice subjunctive mood concepts daily',
    'Review and apply in IELTS practice tests',
    'Write example sentences using key concepts',
    'Teach the concept to someone else to reinforce understanding'
  ],
  backPath: '/exams/english/#grammar',
  backLabel: 'Back to Grammar',
  aiContext: 'Subjunctive Mood: Base verb after suggest/recommend/insist that. Were-subjunctive for hypotheticals. Formal register for IELTS Writing Task 2.'
};

export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...subjunctiveMoodData,
      title: subjunctiveMoodData.title + ' (Foundations)',
      overview: { ...subjunctiveMoodData.overview, duration: '15-20 minutes' },
      sections: subjunctiveMoodData.sections.slice(0, Math.max(2, Math.ceil(subjunctiveMoodData.sections.length / 2))),
      exercises: subjunctiveMoodData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...subjunctiveMoodData,
      title: subjunctiveMoodData.title + ' (Advanced)',
      overview: { ...subjunctiveMoodData.overview, duration: '30-35 minutes' },
      sections: subjunctiveMoodData.sections,
      exercises: subjunctiveMoodData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(subjunctiveMoodData.tips || []),
        'For Band 8+: Focus on natural-sounding usage rather than just accuracy.',
        'Advanced tip: Integrate this concept fluidly in timed IELTS tasks.',
      ],
    };
  }
  return subjunctiveMoodData;
}
